import { useState } from 'react';
import { TrendingUp, Tag } from 'lucide-react';

const fmt = (a) => new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD', maximumFractionDigits: 0 }).format(a || 0);

const TAG_COLORS = {
  'one-time': 'bg-purple-100 text-purple-700',
  'monthly': 'bg-blue-100 text-blue-700',
  'yearly': 'bg-green-100 text-green-700',
  'paid': 'bg-emerald-100 text-emerald-700',
  'pending': 'bg-yellow-100 text-yellow-700',
  'subscription': 'bg-indigo-100 text-indigo-700',
  'cc-fee': 'bg-orange-100 text-orange-700',
};

export function IncomeBreakdown({ items }) {
  const [activeTags, setActiveTags] = useState([]);

  if (!items || items.length === 0) return null;

  const allTags = [...new Set(items.flatMap(i => i.tags || []))];
  const filtered = activeTags.length > 0 ? items.filter(i => (i.tags || []).some(t => activeTags.includes(t))) : items;
  const filteredTotal = filtered.reduce((s, i) => s + (i.total || 0), 0);
  const toggle = (t) => setActiveTags(p => p.includes(t) ? p.filter(x => x !== t) : [...p, t]);

  return (
    <div className="bg-white rounded-xl border border-gray-200 p-6">
      <div className="flex items-center justify-between mb-4">
        <h3 className="font-bold text-[#0A1628] flex items-center gap-2"><TrendingUp className="w-4 h-4 text-green-600" /> Income Breakdown</h3>
        <span className="text-sm font-bold text-green-600">{fmt(filteredTotal)}</span>
      </div>
      {allTags.length > 0 && <TagBar tags={allTags} active={activeTags} toggle={toggle} clear={() => setActiveTags([])} />}
      <div className="mt-4 space-y-0 divide-y">
        {filtered.map((item, i) => <IncomeRow key={i} item={item} />)}
      </div>
    </div>
  );
}

function IncomeRow({ item }) {
  return (
    <div className="flex items-center justify-between py-3">
      <div className="flex-1 min-w-0">
        <p className="font-medium text-sm text-[#0A1628] truncate">{item.client_name}</p>
        <div className="flex gap-1 mt-1">{(item.tags || []).map(t => <span key={t} className={`px-1.5 py-0.5 rounded text-[10px] font-semibold ${TAG_COLORS[t] || 'bg-gray-100 text-gray-600'}`}>{t}</span>)}</div>
      </div>
      <div className="text-right text-xs space-y-0.5 flex-shrink-0 ml-4">
        {item.onetime > 0 && <p className="text-purple-600">{fmt(item.onetime)} <span className="text-gray-400">1x</span></p>}
        {item.monthly > 0 && <p className="text-blue-600">{fmt(item.monthly)} <span className="text-gray-400">/mo</span></p>}
        {item.yearly > 0 && <p className="text-green-600">{fmt(item.yearly)} <span className="text-gray-400">/yr</span></p>}
        {item.cc_fees > 0 && <p className="text-orange-600">{fmt(item.cc_fees)} <span className="text-gray-400">CC fee</span></p>}
        <p className="font-bold text-[#0A1628]">{fmt(item.total + (item.cc_fees || 0))}</p>
      </div>
    </div>
  );
}

function TagBar({ tags, active, toggle, clear }) {
  return (
    <div className="flex items-center gap-1.5 flex-wrap">
      <Tag className="w-3 h-3 text-gray-400" />
      {tags.map(t => (
        <button key={t} onClick={() => toggle(t)} className={`px-2 py-1 rounded-md text-xs font-medium transition-colors ${active.includes(t) ? 'bg-[#014DB7] text-white' : TAG_COLORS[t] || 'bg-gray-100 text-gray-600'}`}>{t}</button>
      ))}
      {active.length > 0 && <button onClick={clear} className="text-[10px] text-red-500 hover:underline ml-1">Clear</button>}
    </div>
  );
}
